import React, { createContext, useContext } from 'react';
import { v4 as uuid } from 'uuid';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { arrayUtils } from '@indico-data/utils';
import { ListItem } from './index';
import { useItemSwap } from './useItemSwap';
const droppableId = uuid();
const ListControlsContext = createContext({});
ListControlsContext.displayName = 'ReorderableListControls';
/**
 * Vertical list that may be reordered by drag-and-drop action, or programmatically by either
 * including `<ListControls>` in the custom component to be passed in (`ListItemComponent`), or
 * by using the `useListItem` Context hook directly - see `ListItemContextType` at the bottom
 * of this file for a list of the props available from the hook.
 *
 * @see {@link https://reactjs.org/docs/context.html}
 */
export function ReorderableList(props) {
    const { className, id, items, itemsVerticalGap = 0, listControls, onDelete, onReorder, swapDuration = 250, ListItemComponent, } = props;
    const { addItemHeight, clearSwapAnimations, setSwapAnimations, swapAnimations } = useItemSwap();
    const handleDelete = (itemId) => {
        const itemIndex = items.findIndex((item) => itemId === item.id);
        const updatedItems = arrayUtils.deleteItem(items, itemIndex);
        onReorder(updatedItems);
        onDelete && onDelete(updatedItems);
    };
    // only ever called as a result of a Drop action
    const handleDragEnd = (result) => {
        if (!result.destination || result.destination.index === result.source.index) {
            return;
        }
        const reorderedItems = arrayUtils.reorderItem(items, result.source.index, result.destination.index);
        onReorder(reorderedItems);
    };
    // called when programmatically changing order, e.g. pressing an up/down arrow button in `ListControls`
    const handleMoveItem = (sourceId, direction) => {
        const sourceIndex = items.findIndex((item) => item.id === sourceId);
        if ((sourceIndex === 0 && direction === 'up') ||
            (sourceIndex === items.length && direction === 'down')) {
            return;
        }
        const destinationIndex = direction === 'up' ? sourceIndex - 1 : sourceIndex + 1;
        const destinationId = items[destinationIndex].id;
        setSwapAnimations(sourceId, destinationId, direction);
        const reorderedItems = arrayUtils.reorderItem(items, sourceIndex, destinationIndex);
        setTimeout(() => {
            onReorder(reorderedItems);
            clearSwapAnimations();
        }, swapDuration + 100); // jank protection: wait an extra 100ms before the css classes are removed
    };
    return (React.createElement(DragDropContext, { onDragEnd: handleDragEnd },
        React.createElement(Droppable, { droppableId: droppableId }, (provided) => (React.createElement("ul", Object.assign({ ref: provided.innerRef, className: className, "data-cy": props['data-cy'], id: id }, provided.droppableProps),
            React.createElement(ListControlsContext.Provider, { value: Object.assign({}, listControls) }, items.map((item, i) => (React.createElement(ListItem, { animating: swapAnimations.has(item.id) ? swapAnimations.get(item.id) : null, addItemHeight: addItemHeight, key: item.id, deleteItem: handleDelete, item: item, index: i, itemsVerticalGap: itemsVerticalGap, listLength: items.length, moveItem: (direction) => handleMoveItem(item.id, direction), swapDuration: swapDuration, InnerComponent: ListItemComponent })))),
            provided.placeholder)))));
}
export function useListControls() {
    return useContext(ListControlsContext);
}
//# sourceMappingURL=ReorderableList.js.map